// Raporlama.js

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { collection, getDocs, doc, getDoc } from 'firebase/firestore';
import { database } from './firebaseConfig';
import * as XLSX from 'xlsx-js-style';
import { Download, Calendar, Filter, FileText } from 'lucide-react';
import DatePicker, { registerLocale } from 'react-datepicker';
import tr from 'date-fns/locale/tr';
import Select from 'react-select';
import { debounce } from 'lodash';
import 'react-datepicker/dist/react-datepicker.css';
import './Raporlama.css';
import { auth } from './firebaseConfig';
import { toTurkishUpperCase } from '../utils/searchUtils';
import { formatTrDateOnly } from '../utils/hopsDate';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';

registerLocale('tr', tr);

const parseDate = (dateString) => {
  if (!dateString) return new Date();
  const [day, month, year] = dateString.split('.');
  if (!day || !month || !year) return new Date();
  return new Date(`${year}-${month}-${day}T00:00:00`);
};

const Raporlama = () => {
  // Durum Yönetimi
  const [workData, setWorkData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Filtre Durumları
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [customerOptions, setCustomerOptions] = useState([]);
  const [selectedCustomers, setSelectedCustomers] = useState([]);
  const [machineOptions, setMachineOptions] = useState([]);
  const [selectedMachines, setSelectedMachines] = useState([]);
  const [operatorOptions, setOperatorOptions] = useState([]);
  const [selectedOperators, setSelectedOperators] = useState([]);

  // Sıralama Durumları
  const [order, setOrder] = useState('asc'); // 'asc' veya 'desc'
  const [orderBy, setOrderBy] = useState('Müşteri Adı'); // Varsayılan sıralama sütunu

  // Mesai Raporu Durumları
  const [isMesaiReportModalOpen, setIsMesaiReportModalOpen] = useState(false);
  const [selectedOperatorForMesai, setSelectedOperatorForMesai] = useState(null);

  // Debounced Fonksiyonlar
  const debouncedSetStartDate = useMemo(
    () => debounce((date) => {
      console.log('Setting start date:', date);
      setStartDate(date);
    }, 300),
    []
  );

  const debouncedSetEndDate = useMemo(
    () => debounce((date) => {
      console.log('Setting end date:', date);
      setEndDate(date);
    }, 300),
    []
  );

  // Zaman Formatlama Fonksiyonu
  const formatTime = useCallback((timeString) => {
    if (!timeString || timeString === '""') return null;
    const cleanTime = timeString.replace(/['"]/g, '').trim();
    if (!cleanTime) return null;
    const [hours, minutes] = cleanTime.split(':').map(num => num.trim());
    if (!hours || !minutes) return null;
    return `${hours.padStart(2, '0')}:${minutes.padStart(2, '0')}`;
  }, []);

  // Süre Hesaplama Fonksiyonu
  const calculateDuration = useCallback((start, end) => {
    if (!start || !end) return 0;
    const [startHours, startMinutes] = start.split(':').map(Number);
    const [endHours, endMinutes] = end.split(':').map(Number);
    let duration = (endHours * 60 + endMinutes) - (startHours * 60 + startMinutes);
    if (duration < 0) {
      duration += 24 * 60;
    }
    return duration;
  }, []);

  // YENİ: Gerçek çalışma saatini hesaplama fonksiyonu
  const calculateActualWorkHours = useCallback((item) => {
    let totalMinutes = 0;
    
    // 1. Vardiya hesapla
    const start1 = formatTime(item['1. Başlangıç Saati']);
    const end1 = formatTime(item['1. Bitiş Saati']);
    if (start1 && end1) {
      totalMinutes += calculateDuration(start1, end1);
    }
    
    // 2. Vardiya hesapla
    const start2 = formatTime(item['2. Başlangıç Saati']);
    const end2 = formatTime(item['2. Bitiş Saati']);
    if (start2 && end2) {
      totalMinutes += calculateDuration(start2, end2);
    }
    
    // Dakikaları saate çevir
    return totalMinutes / 60;
  }, [formatTime, calculateDuration]);

  // Saat formatını HH:MM'ye çeviren yardımcı fonksiyon
  const formatHoursToHHMM = useCallback((decimalHours) => {
    if (!decimalHours || decimalHours === 0) return '00:00';
    const hours = Math.floor(decimalHours);
    const minutes = Math.round((decimalHours % 1) * 60);
    return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
  }, []);

  // YENİ: Cari Kasa mantığıyla müşteri bakiyesini çekme fonksiyonu
  const fetchCustomerDebtBalance = useCallback(async (customerName) => {
    try {
      if (!auth.currentUser?.email || !customerName) {
        return 0;
      }

      // Müşteri adından müşteri ID'sini bul
      const customersRef = collection(database, `users/${auth.currentUser.email}/customerList`);
      const customersSnapshot = await getDocs(customersRef);
      
      let customerId = null;
      customersSnapshot.docs.forEach(doc => {
        const customerData = doc.data();
        if (customerData['Müşteri Adı'] === customerName || 
            customerData.customerName === customerName ||
            customerData.name === customerName) {
          customerId = doc.id;
        }
      });

      if (!customerId) {
        console.warn(`Müşteri bulunamadı: ${customerName}`);
        return 0;
      }

      // Cari kasa bakiyesini çek
      const debtRef = doc(database, `users/${auth.currentUser.email}/customerList/${customerId}/debt/current`);
      const debtDoc = await getDoc(debtRef);
      
      if (debtDoc.exists()) {
        const debtData = debtDoc.data();
        const debtHistory = debtData.history || [];
        
        // Cari Kasa mantığı: Önce amount kontrol et, 0 ise history'den hesapla
        const computedAmount = (() => {
          // Önce Firebase'deki amount'ı kontrol et
          if (typeof debtData.amount === 'number' && debtData.amount !== 0) {
            return debtData.amount;
          }
          
          // Eğer amount 0 ise veya yoksa, history'den hesapla
          if (debtHistory.length === 0) return 0;
          
          // Cari Kasa mantığı: Tüm amount'ları topla (isInfoOnly olanları hariç)
          return debtHistory
            .filter(h => !h.isInfoOnly) // Sadece bilgi amaçlı olanları hariç tut
            .reduce((tot, h) => {
              const amount = parseFloat(h.amount || 0);
              return tot + amount; // Direkt topla, type'a bakma
            }, 0);
        })();
        
        console.log(`💰 ${customerName} - amount: ${debtData.amount}, history hesaplanan: ${computedAmount}, kullanılan: ${computedAmount} TL`);
        return computedAmount;
      }
      
      return 0;
    } catch (error) {
      console.error(`Cari kasa bakiyesi çekilirken hata (${customerName}):`, error);
      return 0;
    }
  }, []);

  // Toplam Çalışma Saatini Hesaplama
  const calculateTotalWorkHours = useCallback((item) => {
    // Sadece field'ların varlığını kontrol et, hesaplama yapma
    const start1 = item['1. Başlangıç Saati'];
    const end1 = item['1. Bitiş Saati'];
    const start2 = item['2. Başlangıç Saati'];
    const end2 = item['2. Bitiş Saati'];

    console.log(`Checking work hours for item ID: ${item.id}`);
    console.log('Start1:', start1, 'End1:', end1, 'Start2:', start2, 'End2:', end2);

    // Eğer herhangi bir saat field'ı varsa 1 döndür, yoksa 0
    const hasWorkHours = start1 || end1 || start2 || end2;
    
    if (!hasWorkHours) {
      console.warn(`Item ID: ${item.id} has no valid working hours.`);
      return 0;
    }

    console.log(`Item ID: ${item.id} has work hours.`);
    return 1; // Sadece varlık kontrolü yap
  }, []);

  // YENİ: Son puantaj tarihini bulma fonksiyonu
  const getLastPuantajDate = useCallback((customerName, machineName, data) => {
    const relevantPuantajlar = data.filter(item => 
      item['Müşteri Adı'] === customerName && 
      item['Makine İsmi'] === machineName
    );
    
    if (relevantPuantajlar.length === 0) return null;
    
    // Tarihleri sırala ve en son olanı al
    const sortedDates = relevantPuantajlar
      .map(item => parseDate(item['Tarih']))
      .sort((a, b) => b - a); // En yeni tarih önce
    
    return sortedDates[0];
  }, []);

  // Türkçe karakter dönüştürme fonksiyonu
  const convertTurkishChars = useCallback((text) => {
    if (!text || typeof text !== 'string') return text;
    
    const charMap = {
      'ç': 'c', 'Ç': 'C',
      'ğ': 'g', 'Ğ': 'G', 
      'ı': 'i', 'İ': 'I',
      'ö': 'o', 'Ö': 'O',
      'ş': 's', 'Ş': 'S',
      'ü': 'u', 'Ü': 'U',
      'â': 'a', 'Â': 'A',
      'î': 'i', 'Î': 'I',
      'û': 'u', 'Û': 'U'
    };
    
    return text.replace(/[çÇğĞıİöÖşŞüÜâÂîÎûÛ]/g, (match) => charMap[match] || match);
  }, []);

  const [userEmail, setUserEmail] = useState('');
  const [companyName, setCompanyName] = useState('');

  // Veri Çekme ve İşleme
  useEffect(() => {
    const fetchData = async () => {
      // Mevcut kullanıcı bilgisini al
      const currentUser = auth.currentUser;
      if (!currentUser || !currentUser.email) {
        setError("Kullanıcı bilgisi alınamadı. Lütfen tekrar giriş yapın.");
        setLoading(false);
        return;
      }

      setUserEmail(currentUser.email);

      try {
        // Firma bilgisini al
        const userDocRef = doc(database, 'users', currentUser.email);
        const userDoc = await getDoc(userDocRef);
        
        if (userDoc.exists()) {
          const userData = userDoc.data();
          setCompanyName(userData.companyName || 'Firma Adı Yok');
        }
        
        // Referansları al
        const customerListRef = collection(database, `users/${currentUser.email}/customerList`);
        const machinesRef = collection(database, `users/${currentUser.email}/machines`);
        const operatorsRef = collection(database, `users/${currentUser.email}/operators`);
        
        // Verileri çek
        const [customerSnapshot, machineSnapshot, operatorSnapshot] = await Promise.all([
          getDocs(customerListRef),
          getDocs(machinesRef),
          getDocs(operatorsRef)
        ]);

        // Müşteri listesini yükle
        const customerOptionsArray = customerSnapshot.docs
          .map(doc => {
            const data = doc.data();
            const customerName = data['Müşteri Adı']?.trim() || '';
            const cariCode = data['cariCode']?.trim() || '';
            return {
              value: customerName,
              label: `${customerName} / ${cariCode}`,
              searchValue: customerName.toLowerCase()
            };
          })
          .filter(option => option.value)
          .sort((a, b) => a.label.localeCompare(b.label));
        setCustomerOptions(customerOptionsArray);
        console.log('Fetched customer options:', customerOptionsArray);

        // Makine listesini yükle
        const machineOptionsArray = machineSnapshot.docs
          .map(doc => {
            const data = doc.data();
            const machineName = data['MakineAdı']?.trim() || '';
            return {
              value: machineName,
              label: machineName,
              hourlyRate: Number(data['BirimFiyat']) || 0,
              searchValue: machineName.toLowerCase()
            };
          })
          .filter(option => option.value)
          .sort((a, b) => a.label.localeCompare(b.label));
        setMachineOptions(machineOptionsArray);
        console.log('Fetched machine options:', machineOptionsArray);

        // Operatör listesini yükle
        const operatorOptionsArray = operatorSnapshot.docs
          .map(doc => {
            const data = doc.data();
            const firstName = data['name']?.trim() || '';
            const lastName = data['surname']?.trim() || '';
            // Ad ve soyad varsa birleştir, yoksa sadece adı kullan
            const fullName = lastName ? `${firstName} ${lastName}` : firstName;
            
            return {
              value: fullName,
              label: fullName,
              searchValue: fullName.toLowerCase()
            };
          })
          .filter(option => option.value)
          .sort((a, b) => a.label.localeCompare(b.label));
        setOperatorOptions(operatorOptionsArray);
        console.log('Fetched operator options:', operatorOptionsArray);

        // Puantaj verilerini yükle - sadece kendi firma verilerini
        const puantajlarRef = collection(database, `users/${currentUser.email}/timeSheets`);
        const puantajSnapshot = await getDocs(puantajlarRef);
        const puantajData = puantajSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        setWorkData(puantajData);
        setLoading(false);
        console.log('Fetched puantajlar data:', puantajData);

      } catch (error) {
        console.error('Veri yükleme hatası:', error);
        setError('Veriler yüklenirken bir hata oluştu');
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  // Veri Filtreleme
  const filteredData = useMemo(() => {
    console.log('Applying filters to workData.');
    let filtered = workData;

    if (startDate && endDate) {
      filtered = filtered.filter(item => {
        const itemDate = parseDate(item['Tarih']);
        // Başlangıç tarihi (dahil)
        const startOfDay = new Date(startDate);
        startOfDay.setHours(0, 0, 0, 0);
        // Bitiş tarihi (dahil)
        const endOfDay = new Date(endDate);
        endOfDay.setHours(23, 59, 59, 999);
        
        return itemDate >= startOfDay && itemDate <= endOfDay;
      });
      console.log(`Filtered by date range: ${startDate} - ${endDate}, ${filtered.length} records remaining.`);
    }

    if (selectedCustomers.length > 0) {
      filtered = filtered.filter(item => {
        const customerName = (item['Müşteri Adı'] || '').toLowerCase();
        return selectedCustomers.some(selected =>
          selected.value.toLowerCase() === customerName
        );
      });
      console.log(`Filtered by customers: ${selectedCustomers.length} selected, ${filtered.length} records remaining.`);
    }

    if (selectedMachines.length > 0) {
      filtered = filtered.filter(item => {
        const machineName = (item['Makine İsmi'] || '').toLowerCase();
        return selectedMachines.some(selected =>
          selected.value.toLowerCase() === machineName
        );
      });
      console.log(`Filtered by machines: ${selectedMachines.length} selected, ${filtered.length} records remaining.`);
    }

    if (selectedOperators.length > 0) {
      filtered = filtered.filter(item => {
        const operatorName = (item['Operatör Adı'] || '').toLowerCase();
        return selectedOperators.some(selected =>
          selected.value.toLowerCase() === operatorName
        );
      });
      console.log(`Filtered by operators: ${selectedOperators.length} selected, ${filtered.length} records remaining.`);
    }

    console.log(`Total filtered records: ${filtered.length}`);
    return filtered;
  }, [workData, startDate, endDate, selectedCustomers, selectedMachines, selectedOperators]);

  // YENİ: Cari kasa bakiyeli özet veri state'i
  const [summaryData, setSummaryData] = useState([]);
  const [summaryLoading, setSummaryLoading] = useState(false);

    // YENİ: Cari Kasa Mantığına Göre Tüm Müşteriler İçin Özet Veri Oluşturma
  const generateSummaryDataWithDebt = useCallback(async () => {
    console.log('🔄 Cari kasa mantığıyla tüm müşteriler için özet veri oluşturuluyor...');
    setSummaryLoading(true);
    
    try {
      if (!auth.currentUser?.email) {
        console.warn('Kullanıcı oturum bilgisi bulunamadı');
        setSummaryData([]);
        return;
      }

      // İlk aşama: Cari Kasa mantığıyla tüm müşteri listesini çek
      console.log('📋 Tüm müşteri listesi Firebase\'den çekiliyor...');
      const customersRef = collection(database, `users/${auth.currentUser.email}/customerList`);
      const customersSnapshot = await getDocs(customersRef);
      
      const allCustomers = [];
      customersSnapshot.docs.forEach(doc => {
        const customerData = doc.data();
        const onayDurumu = customerData['Onay'];
        // Cari Kasa mantığı: Onaylanmış ve onay bekleyen müşterileri dahil et
        if (onayDurumu === 'Onaylandı' || onayDurumu === 'Onay Bekliyor' || onayDurumu === 'Bekleniyor') {
          allCustomers.push({
            id: doc.id,
            name: customerData['Müşteri Adı'] || '',
            ...customerData
          });
        }
      });

      console.log(`📋 Toplam ${allCustomers.length} onaylanmış müşteri bulundu`);

      // İkinci aşama: Her müşteri için son puantaj bilgilerini filteredData'dan al
      const summary = {};
      
      allCustomers.forEach(customer => {
        const customerName = customer.name;
        summary[customerName] = {
          customer: customerName,
          lastMachine: '-', // Varsayılan değer
          recordCount: 0,
          amount: 0, // Bu cari kasa bakiyesi olacak
          lastPuantajDate: null
        };
      });

      // Üçüncü aşama: filteredData'dan puantaj bilgilerini güncelle (filtre mantığı korunuyor)
      filteredData.forEach(item => {
        const customerName = item['Müşteri Adı'];
        if (summary[customerName]) {
          const hasWorkHours = calculateTotalWorkHours(item);
          if (hasWorkHours > 0) {
            summary[customerName].recordCount += 1;
            
            // Son puantaj tarihini ve makineyi güncelle
            const itemDate = parseDate(item['Tarih']);
            if (!summary[customerName].lastPuantajDate || itemDate > summary[customerName].lastPuantajDate) {
              summary[customerName].lastPuantajDate = itemDate;
              summary[customerName].lastMachine = item['Makine İsmi'] || '-';
            }
          }
        }
      });

      // Dördüncü aşama: Tüm müşteriler için cari kasa bakiyesini çek
      const summaryArray = Object.values(summary);
      console.log(`💰 ${summaryArray.length} müşteri için cari kasa bakiyesi çekiliyor...`);
      
      // Tüm müşterilerin bakiyelerini paralel olarak çek
      const debtBalances = await Promise.all(
        summaryArray.map(item => 
          fetchCustomerDebtBalance(item.customer).then(balance => ({
            customerName: item.customer,
            balance
          }))
        )
      );
      
      // Bakiye bilgilerini bir map'e dönüştür
      const balanceMap = {};
      debtBalances.forEach(({ customerName, balance }) => {
        balanceMap[customerName] = balance;
      });

      // Beşinci aşama: Amount değerlerini cari kasa bakiyesi ile güncelle
      summaryArray.forEach(item => {
        item.amount = balanceMap[item.customer] || 0;
        console.log(`💰 ${item.customer}: ${item.amount} TL cari bakiye`);
      });
      
      console.log('✅ Tüm müşteriler için cari kasa bakiyeli özet veri oluşturuldu:', summaryArray);
      setSummaryData(summaryArray);
    } catch (error) {
      console.error('❌ Özet veri oluşturulurken hata:', error);
      setSummaryData([]);
    } finally {
      setSummaryLoading(false);
    }
     }, [filteredData, calculateTotalWorkHours, fetchCustomerDebtBalance]);

  // Filtrelenmiş veri değiştiğinde özet veriyi yeniden oluştur
  // Artık cari kasa mantığı olduğu için filteredData boş olsa bile tüm müşterileri göster
  useEffect(() => {
    generateSummaryDataWithDebt();
  }, [filteredData, generateSummaryDataWithDebt]);

  // YENİ: Toplam Kayıt ve Tutar Hesaplama
  const totalRecords = useMemo(() => {
    const total = summaryData.reduce((total, item) => total + item.recordCount, 0);
    console.log('Total records:', total);
    return total;
  }, [summaryData]);

  const totalAmount = useMemo(() => {
    const total = summaryData.reduce((total, item) => total + item.amount, 0);
    console.log('Total amount:', total);
    return total;
  }, [summaryData]);

  // YENİ: Geliştirilmiş Excel Raporu Oluşturma
  const generateExcelReport = useCallback(() => {
    console.log('Generating enhanced Excel report.');
    
    // Geliştirilmiş özet verileri kullan
    const summarySheet = summaryData.map(data => ({
      'Müşteri': data.customer,
      'Son Çalışmış Makine': data.lastMachine,
      'Son Puantaj Tarihi': data.lastPuantajDate ? formatTrDateOnly(data.lastPuantajDate) : '-',
      'Kayıt Sayısı': data.recordCount,
      'Bakiye (TL)': Number(data.amount).toFixed(2)
    }));

    // Müşteri detay sayfası
    const detailSheet = summaryData.map(data => {
      return {
        'Müşteri': data.customer,
        'Son Çalışmış Makine': data.lastMachine,
        'Son Puantaj Tarihi': data.lastPuantajDate ? formatTrDateOnly(data.lastPuantajDate) : '-',
        'Bakiye (TL)': Number(data.amount).toFixed(2)
      };
    });

    // Ham veri sayfasına gerçek çalışma saatleri de eklendi
    const rawDataSheet = filteredData.map(item => {
      const actualHours = calculateActualWorkHours(item);
      return {
        'Tarih': item['Tarih'],
        'Müşteri': item['Müşteri Adı'],
        'Makine': item['Makine İsmi'],
        'Operatör': item['Operatör Adı'],
        'Çalışma Saati': formatHoursToHHMM(actualHours),
        'Çalışma Detayı': item['Çalışma Detayı'] || '-',
        '1. Başlangıç Saati': item['1. Başlangıç Saati'] || '-',
        '1. Bitiş Saati': item['1. Bitiş Saati'] || '-',
        '2. Başlangıç Saati': item['2. Başlangıç Saati'] || '-',
        '2. Bitiş Saati': item['2. Bitiş Saati'] || '-',
      };
    });

    console.log('Creating Excel sheets with enhanced data.');
    const wb = XLSX.utils.book_new();

    // Segoe UI fontunu kullan [[memory:4232587]]
    const worksheetOptions = {
      '!cols': [
        { wch: 20 }, // Müşteri
        { wch: 20 }, // Son Çalışmış Makine
        { wch: 18 }, // Son Puantaj Tarihi
        { wch: 12 }, // Kayıt Sayısı
        { wch: 15 }  // Bakiye
      ]
    };

    const summaryWs = XLSX.utils.json_to_sheet(summarySheet);
    summaryWs['!cols'] = worksheetOptions['!cols'];
    
    const detailWs = XLSX.utils.json_to_sheet(detailSheet);
    detailWs['!cols'] = [
      { wch: 20 }, // Müşteri
      { wch: 20 }, // Son Çalışmış Makine
      { wch: 18 }, // Son Puantaj Tarihi
      { wch: 15 }  // Bakiye
    ];
    
    const rawWs = XLSX.utils.json_to_sheet(rawDataSheet);
    
    XLSX.utils.book_append_sheet(wb, summaryWs, "Müşteri Özet");
    XLSX.utils.book_append_sheet(wb, detailWs, "Müşteri Detay");
    XLSX.utils.book_append_sheet(wb, rawWs, "Tüm Kayıtlar");

    const dateStr = startDate && endDate ?
      `_${formatTrDateOnly(startDate)}_${formatTrDateOnly(endDate)}`.replace(/\./g, '_') :
      '';
    const fileName = `Puantaj_Raporu${dateStr}.xlsx`;
    console.log(`Writing enhanced Excel file: ${fileName}`);
    XLSX.writeFile(wb, fileName);
  }, [summaryData, filteredData, machineOptions, calculateActualWorkHours, startDate, endDate]);

  // YENİ: PDF Raporu Oluşturma - Cari Kasa PDF raporlarına benzer
  const generatePDFReport = useCallback(() => {
    console.log('Generating PDF report similar to Cari Kasa reports.');
    
    // PDF oluştur
    const doc = new jsPDF();
    
    // Türkçe karakter desteği için font ayarı
    doc.setFont('helvetica');
    
    // Text wrapping fonksiyonu
    const addWrappedText = (text, x, startY, maxWidth, lineHeight = 6) => {
      const words = text.split(' ');
      let line = '';
      let y = startY;
      
      for (let n = 0; n < words.length; n++) {
        const testLine = line + words[n] + ' ';
        const testWidth = doc.getTextWidth(testLine);
        
        if (testWidth > maxWidth && n > 0) {
          doc.text(line.trim(), x, y);
          line = words[n] + ' ';
          y += lineHeight;
        } else {
          line = testLine;
        }
      }
      doc.text(line.trim(), x, y);
      return y + lineHeight;
    };
    
    // Başlık
    doc.setFontSize(16);
    const titleText = convertTurkishChars(`${companyName} - Puantaj Ozet Raporu`);
    let currentY = addWrappedText(titleText, 20, 20, 170, 8);
    
    // Tarih
    doc.setFontSize(12);
    currentY += 5;
    doc.text(`Rapor Tarihi: ${formatTrDateOnly(new Date())}`, 20, currentY);
    currentY += 7;
    
    // Filtre bilgileri
    if (startDate && endDate) {
      doc.text(`Tarih Araligi: ${formatTrDateOnly(startDate)} - ${formatTrDateOnly(endDate)}`, 20, currentY);
      currentY += 7;
    }
    
    currentY += 5;

    // PDF verilerini hazırla
    const pdfData = summaryData.map(data => [
      convertTurkishChars(data.customer),
      convertTurkishChars(data.lastMachine),
      data.lastPuantajDate ? formatTrDateOnly(data.lastPuantajDate) : '-',
      Number(data.amount).toLocaleString('tr-TR', { minimumFractionDigits: 2 })
    ]);

    // Toplam satırını ekle
    pdfData.push([
      'TOPLAM',
      '',
      '',
      Number(totalAmount).toLocaleString('tr-TR', { minimumFractionDigits: 2 })
    ]);

    // Tablo
    autoTable(doc, {
      head: [['Musteri', 'Son Calismis Makine', 'Son Puantaj Tarihi', 'Bakiye (TL)']],
      body: pdfData,
      startY: currentY,
      styles: {
        fontSize: 8,
        cellPadding: 2,
        font: 'helvetica'
      },
      headStyles: {
        fillColor: [41, 128, 185],
        textColor: 255,
        fontStyle: 'bold',
        halign: 'left'
      },
      columnStyles: {
        0: { halign: 'left' },   // Müşteri
        1: { halign: 'left' },   // Son Çalışmış Makine
        2: { halign: 'center' }, // Son Puantaj Tarihi
        3: { halign: 'right' }   // Bakiye
      },
      alternateRowStyles: {
        fillColor: [245, 245, 245]
      },
      didParseCell: function (data) {
        // Toplam satırı için özel stil
        if (data.section === 'body' && data.row.index === pdfData.length - 1) {
          data.cell.styles.fillColor = [220, 220, 220];
          data.cell.styles.fontStyle = 'bold';
          data.cell.styles.textColor = [0, 0, 0];
        }
      }
    });

    // Footer ekleme (Cari Kasa'dan esinlenerek)
    const pageCount = doc.internal.getNumberOfPages();
    const pageWidth = doc.internal.pageSize.width;
    const pageHeight = doc.internal.pageSize.height;
    
    for (let i = 1; i <= pageCount; i++) {
      doc.setPage(i);
      
      doc.setFontSize(8);
      doc.setFont('helvetica', 'italic');
      
      const footerText = 'Bu belge HOPS tarafindan dijital olarak olusturulmustur.';
      const textWidth = doc.getTextWidth(footerText);
      const xPosition = (pageWidth - textWidth) / 2;
      
      doc.text(footerText, xPosition, pageHeight - 10);
      
      doc.setFont('helvetica', 'normal');
      doc.text(`Sayfa ${i}/${pageCount}`, pageWidth - 30, pageHeight - 10);
    }

    // Dosya adını oluştur ve indir
    const dateStr = startDate && endDate ?
      `_${formatTrDateOnly(startDate)}_${formatTrDateOnly(endDate)}`.replace(/\./g, '_') :
      '';
    const fileName = convertTurkishChars(`Puantaj_Ozet_Raporu${dateStr}.pdf`);
    doc.save(fileName);
  }, [summaryData, companyName, startDate, endDate, totalAmount, convertTurkishChars]);

  // Sıralama Fonksiyonu
  const handleSort = useCallback((column) => {
    const isAsc = orderBy === column && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(column);
    console.log(`Sorting by ${column} in ${isAsc ? 'descending' : 'ascending'} order.`);
  }, [orderBy, order]);

  // Veriyi Sıralama
  const sortedData = useMemo(() => {
    console.log('Sorting data.');
    const comparator = (a, b) => {
      let aValue = a[orderBy];
      let bValue = b[orderBy];

      // Tarih sütunu özel işleniyor
      if (orderBy === 'Tarih') {
        aValue = parseDate(aValue);
        bValue = parseDate(bValue);
        if (aValue < bValue) return order === 'asc' ? -1 : 1;
        if (aValue > bValue) return order === 'asc' ? 1 : -1;
        return 0;
      }

      // Diğer sütunlar string karşılaştırması
      if (typeof aValue === 'string') {
        aValue = aValue.toLowerCase();
      }
      if (typeof bValue === 'string') {
        bValue = bValue.toLowerCase();
      }

      if (aValue < bValue) return order === 'asc' ? -1 : 1;
      if (aValue > bValue) return order === 'asc' ? 1 : -1;
      return 0;
    };

    const sorted = [...filteredData].sort(comparator);
    console.log('Sorted data:', sorted);
    return sorted;
  }, [filteredData, order, orderBy]);

  // Filtre Değişikliklerini Yönetme
  const handleCustomerChange = useCallback((selectedOptions) => {
    setSelectedCustomers(selectedOptions || []);
    console.log('Selected customers changed:', selectedOptions);
  }, []);

  const handleMachineChange = useCallback((selectedOptions) => {
    setSelectedMachines(selectedOptions || []);
    console.log('Selected machines changed:', selectedOptions);
  }, []);

  const handleOperatorChange = useCallback((selectedOptions) => {
    setSelectedOperators(selectedOptions || []);
    console.log('Selected operators changed:', selectedOptions);
  }, []);

  // Mesai Raporu Fonksiyonları
  const openMesaiReportModal = () => {
    setIsMesaiReportModalOpen(true);
  };

  const closeMesaiReportModal = () => {
    setIsMesaiReportModalOpen(false);
    setSelectedOperatorForMesai(null);
  };

  const handleOperatorSelectForMesai = (operator) => {
    setSelectedOperatorForMesai(operator);
    generateMesaiReport(operator);
    closeMesaiReportModal();
  };

  // Dakikaları HH:MM formatına çevirme
  const formatMinutesToHHMM = useCallback((minutes) => {
    if (!minutes || minutes === 0) return '00:00';
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return `${hours.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}`;
  }, []);

  // Mesai Raporu Oluşturma
  const generateMesaiReport = useCallback(async (operator) => {
    try {
      console.log('Generating mesai report for operator:', operator);

      // Operatörün mesai saatlerini al
      const currentUser = auth.currentUser;
      if (!currentUser?.email) {
        alert('Kullanıcı oturumu bulunamadı.');
        return;
      }

      const operatorsRef = collection(database, `users/${currentUser.email}/operators`);
      const operatorSnapshot = await getDocs(operatorsRef);
      
      let operatorMesaiData = null;
      operatorSnapshot.docs.forEach(doc => {
        const data = doc.data();
        const firstName = data['name']?.trim() || '';
        const lastName = data['surname']?.trim() || '';
        const fullName = lastName ? `${firstName} ${lastName}` : firstName;
        
        if (fullName === operator.value) {
          operatorMesaiData = {
            mesaiBaslangic: data['mesaiBaslangic'] || '09:00',
            mesaiBitis: data['mesaiBitis'] || '18:00'
          };
        }
      });

      if (!operatorMesaiData) {
        alert('Operatörün mesai saatleri bulunamadı.');
        return;
      }

      // Seçilen operatörün puantaj verilerini filtrele
      const operatorData = workData.filter(item => {
        const operatorName = item['Operatör Adı']?.trim() || '';
        return operatorName === operator.value;
      });

      if (operatorData.length === 0) {
        alert('Bu operatör için puantaj verisi bulunamadı.');
        return;
      }

      // Tarih aralığına göre filtreleme (eğer seçili ise)
      let filteredOperatorData = operatorData;
      if (startDate && endDate) {
        filteredOperatorData = operatorData.filter(item => {
          const itemDate = parseDate(item['Tarih']);
          const startOfDay = new Date(startDate);
          startOfDay.setHours(0, 0, 0, 0);
          const endOfDay = new Date(endDate);
          endOfDay.setHours(23, 59, 59, 999);
          
          return itemDate >= startOfDay && itemDate <= endOfDay;
        });
      }

      // Tarih bazlı gruplandırma ve sıralama
      const groupedData = filteredOperatorData.reduce((acc, item) => {
        const date = item['Tarih'];
        if (!acc[date]) {
          acc[date] = [];
        }
        acc[date].push(item);
        return acc;
      }, {});

      // Fazla mesai hesaplama fonksiyonu
      const calculateOvertime = (startTime, endTime, mesaiBaslangic, mesaiBitis) => {
        const [startHour, startMin] = startTime.split(':').map(Number);
        const [endHour, endMin] = endTime.split(':').map(Number);
        const [mesaiStartHour, mesaiStartMin] = mesaiBaslangic.split(':').map(Number);
        const [mesaiEndHour, mesaiEndMin] = mesaiBitis.split(':').map(Number);

        const startMinutes = startHour * 60 + startMin;
        const endMinutes = endHour * 60 + endMin;
        const mesaiStartMinutes = mesaiStartHour * 60 + mesaiStartMin;
        const mesaiEndMinutes = mesaiEndHour * 60 + mesaiEndMin;

        let overtimeMinutes = 0;

        // Mesai başından önce çalışma
        if (startMinutes < mesaiStartMinutes) {
          overtimeMinutes += mesaiStartMinutes - startMinutes;
        }

        // Mesai sonundan sonra çalışma
        if (endMinutes > mesaiEndMinutes) {
          overtimeMinutes += endMinutes - mesaiEndMinutes;
        }

        return overtimeMinutes;
      };

      // Excel raporu için veri hazırlama - Sadece fazla mesai olan kayıtlar
      const reportData = [];
      
      Object.keys(groupedData)
        .sort((a, b) => parseDate(a) - parseDate(b))
        .forEach(date => {
          const dailyEntries = groupedData[date];
          
          dailyEntries.forEach(entry => {
            const start1 = formatTime(entry['1. Başlangıç Saati']);
            const end1 = formatTime(entry['1. Bitiş Saati']);
            const start2 = formatTime(entry['2. Başlangıç Saati']);
            const end2 = formatTime(entry['2. Bitiş Saati']);

            // 1. Vardiya kontrolü
            if (start1 && end1) {
              const overtimeMinutes = calculateOvertime(start1, end1, operatorMesaiData.mesaiBaslangic, operatorMesaiData.mesaiBitis);
              
              if (overtimeMinutes > 0) {
                reportData.push({
                  Tarih: date,
                  'Müşteri Adı': entry['Müşteri Adı'] || '',
                  'Makine Adı': entry['Makine İsmi'] || entry['MakineIsmi'] || entry['MakineAdı'] || '',
                  Vardiya: '1. Vardiya',
                  'Başlangıç Saati': start1,
                  'Bitiş Saati': end1,
                  'Çalışma Süresi': formatMinutesToHHMM(calculateDuration(start1, end1)),
                  'Fazla Mesai Süresi': formatMinutesToHHMM(overtimeMinutes)
                });
              }
            }

            // 2. Vardiya kontrolü
            if (start2 && end2) {
              const overtimeMinutes = calculateOvertime(start2, end2, operatorMesaiData.mesaiBaslangic, operatorMesaiData.mesaiBitis);
              
              if (overtimeMinutes > 0) {
                reportData.push({
                  Tarih: date,
                  'Müşteri Adı': entry['Müşteri Adı'] || '',
                  'Makine Adı': entry['Makine İsmi'] || entry['MakineIsmi'] || entry['MakineAdı'] || '',
                  Vardiya: '2. Vardiya',
                  'Başlangıç Saati': start2,
                  'Bitiş Saati': end2,
                  'Çalışma Süresi': formatMinutesToHHMM(calculateDuration(start2, end2)),
                  'Fazla Mesai Süresi': formatMinutesToHHMM(overtimeMinutes)
                });
              }
            }
          });
        });

      if (reportData.length === 0) {
        const dateRangeText = startDate && endDate 
          ? ` (${formatTrDateOnly(startDate)} - ${formatTrDateOnly(endDate)} tarihleri arasında)`
          : '';
        alert(`${operator.value} operatörünün mesai saatleri (${operatorMesaiData.mesaiBaslangic} - ${operatorMesaiData.mesaiBitis}) dışında çalışma${dateRangeText} bulunamadı.`);
        return;
      }

      // Excel dosyası oluşturma
      exportMesaiReportToExcel(reportData, operator.value, formatMinutesToHHMM);

    } catch (error) {
      console.error('Mesai raporu oluşturulurken hata:', error);
      alert('Mesai raporu oluşturulurken bir hata oluştu.');
    }
  }, [workData, formatTime, calculateDuration, formatMinutesToHHMM, startDate, endDate]);

  // Excel Export Fonksiyonu
  const exportMesaiReportToExcel = (data, operatorName, formatMinutesToHHMM) => {
    try {
      // Toplam fazla mesai süresini hesapla
      const totalOvertimeMinutes = data.reduce((total, row) => {
        const overtimeString = row['Fazla Mesai Süresi'];
        if (overtimeString && overtimeString !== '00:00') {
          const [hours, minutes] = overtimeString.split(':').map(Number);
          return total + (hours * 60) + minutes;
        }
        return total;
      }, 0);

      // Toplam satırını ekle
      const summaryRow = {
        Tarih: operatorName,
        'Müşteri Adı': 'Toplam Süre',
        'Makine Adı': '',
        Vardiya: '',
        'Başlangıç Saati': '',
        'Bitiş Saati': '',
        'Çalışma Süresi': '',
        'Fazla Mesai Süresi': formatMinutesToHHMM(totalOvertimeMinutes)
      };

      const dataWithSummary = [...data, summaryRow];

      // Başlık stilleri
      const headerStyle = {
        font: { name: 'Segoe UI', bold: true, color: { rgb: 'FFFFFF' }, size: 11 },
        fill: { fgColor: { rgb: '366092' } },
        alignment: { horizontal: 'center', vertical: 'center' },
        border: {
          top: { style: 'thin', color: { rgb: '000000' } },
          bottom: { style: 'thin', color: { rgb: '000000' } },
          left: { style: 'thin', color: { rgb: '000000' } },
          right: { style: 'thin', color: { rgb: '000000' } }
        }
      };

      // Veri stilleri
      const dataStyle = {
        font: { name: 'Segoe UI', size: 10 },
        alignment: { horizontal: 'center', vertical: 'center', wrapText: true },
        border: {
          top: { style: 'thin', color: { rgb: 'CCCCCC' } },
          bottom: { style: 'thin', color: { rgb: 'CCCCCC' } },
          left: { style: 'thin', color: { rgb: 'CCCCCC' } },
          right: { style: 'thin', color: { rgb: 'CCCCCC' } }
        }
      };

      // Toplam satırı stili
      const summaryStyle = {
        font: { name: 'Segoe UI', bold: true, size: 11 },
        alignment: { horizontal: 'center', vertical: 'center' },
        fill: { fgColor: { rgb: 'F0F0F0' } },
        border: {
          top: { style: 'thick', color: { rgb: '000000' } },
          bottom: { style: 'thick', color: { rgb: '000000' } },
          left: { style: 'thin', color: { rgb: '000000' } },
          right: { style: 'thin', color: { rgb: '000000' } }
        }
      };

      // Worksheet oluştur
      const ws = XLSX.utils.json_to_sheet(dataWithSummary);

      // Sütun genişlikleri
      const columnWidths = [
        { wch: 15 }, // Tarih (operatör adı için biraz daha geniş)
        { wch: 25 }, // Müşteri Adı
        { wch: 20 }, // Makine Adı
        { wch: 12 }, // Vardiya
        { wch: 12 }, // Başlangıç Saati
        { wch: 12 }, // Bitiş Saati
        { wch: 15 }, // Çalışma Süresi
        { wch: 15 }  // Fazla Mesai Süresi
      ];
      ws['!cols'] = columnWidths;

      // Stil uygulama
      const range = XLSX.utils.decode_range(ws['!ref']);
      for (let C = range.s.c; C <= range.e.c; ++C) {
        // Başlık satırı (ilk satır)
        const headerCell = XLSX.utils.encode_cell({ r: 0, c: C });
        if (ws[headerCell]) {
          ws[headerCell].s = headerStyle;
        }

        // Veri satırları
        for (let R = 1; R <= range.e.r; ++R) {
          const cellAddress = XLSX.utils.encode_cell({ r: R, c: C });
          if (ws[cellAddress]) {
            // Son satır (toplam satırı) için özel stil
            if (R === range.e.r) {
              ws[cellAddress].s = summaryStyle;
            } else {
              ws[cellAddress].s = dataStyle;
            }
          }
        }
      }

      // Workbook oluştur
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Mesai Raporu');

      // Dosya adı
      const fileName = `${operatorName}_Mesai_Raporu_${formatTrDateOnly(new Date())}.xlsx`;

      // Dosyayı indirme
      XLSX.writeFile(wb, fileName);

    } catch (error) {
      console.error('Excel export hatası:', error);
      alert('Excel dosyası oluşturulurken bir hata oluştu.');
    }
  };

  // Loading ve Error Durumları
  if (loading) {
    console.log('Raporlama component is loading.');
    return <div className="loading">Yükleniyor...</div>;
  }

  if (error) {
    console.error('Raporlama component encountered an error:', error);
    return <div className="error">{error}</div>;
  }

  // Sıralama İkonları için Yardımcı Fonksiyon
  const renderSortIcon = (column) => {
    if (orderBy !== column) return null;
    return order === 'asc' ? ' 🔼' : ' 🔽';
  };

  return (
    <div className="raporlama-container">
      <div className="company-info">
        <h2>Puantaj Raporu</h2>
        <div className="user-details">
          <p><strong>Firma:</strong> {companyName}</p>
          <p><strong>E-posta:</strong> {userEmail}</p>
        </div>
      </div>

      <div className="filter-container">
        <div className="filters-row">
          <div className="date-filter">
            <label>
              <Calendar size={16} />
              Tarih Aralığı:
            </label>
            <div className="date-pickers">
              <DatePicker
                selected={startDate}
                onChange={debouncedSetStartDate}
                selectsStart
                startDate={startDate}
                endDate={endDate}
                dateFormat="dd.MM.yyyy"
                placeholderText="Başlangıç Tarihi"
                locale="tr"
              />
              <DatePicker
                selected={endDate}
                onChange={debouncedSetEndDate}
                selectsEnd
                startDate={startDate}
                endDate={endDate}
                minDate={startDate}
                dateFormat="dd.MM.yyyy"
                placeholderText="Bitiş Tarihi"
                locale="tr"
              />
            </div>
          </div>

          <div className="select-filter">
            <label>
              <Filter size={16} />
              Müşteri:
            </label>
            <Select
              isMulti
              options={customerOptions}
              value={selectedCustomers}
              onChange={handleCustomerChange}
              onInputChange={(inputValue, actionMeta) => {
                if (actionMeta.action === 'input-change') {
                  return toTurkishUpperCase(inputValue);
                }
                return inputValue;
              }}
              placeholder="Müşteri Seçin"
              className="react-select"
              classNamePrefix="select"
            />
          </div>

          <div className="select-filter">
            <label>
              <Filter size={16} />
              Makine:
            </label>
            <Select
              isMulti
              options={machineOptions}
              value={selectedMachines}
              onChange={handleMachineChange}
              onInputChange={(inputValue, actionMeta) => {
                if (actionMeta.action === 'input-change') {
                  return toTurkishUpperCase(inputValue);
                }
                return inputValue;
              }}
              placeholder="Makine Seçin"
              className="react-select"
              classNamePrefix="select"
            />
          </div>

          <div className="select-filter">
            <label>
              <Filter size={16} />
              Operatör:
            </label>
            <Select
              isMulti
              options={operatorOptions}
              value={selectedOperators}
              onChange={handleOperatorChange}
              onInputChange={(inputValue, actionMeta) => {
                if (actionMeta.action === 'input-change') {
                  return toTurkishUpperCase(inputValue);
                }
                return inputValue;
              }}
              placeholder="Operatör Seçin"
              className="react-select"
              classNamePrefix="select"
            />
          </div>
        </div>

        <div className="report-buttons-container">
          <button
            className="generate-report-btn excel-btn"
            onClick={generateExcelReport}
            disabled={loading || filteredData.length === 0}
          >
            <Download size={16} />
            Excel Raporu Oluştur
          </button>
          
          <button
            className="generate-report-btn pdf-btn"
            onClick={generatePDFReport}
            disabled={loading || filteredData.length === 0}
          >
            <FileText size={16} />
            PDF Raporu Oluştur
          </button>

          <button
            className="generate-report-btn mesai-btn"
            onClick={openMesaiReportModal}
            disabled={loading || operatorOptions.length === 0}
          >
            <Calendar size={16} />
            Mesai Raporu
          </button>
        </div>
      </div>

      <div className="preview-section">
        <h3>Rapor Önizleme</h3>
        <div className="preview-table">
          <table>
            <thead>
              <tr>
                <th>Müşteri</th>
                <th>Son Çalışmış Makine</th>
                <th>Son Puantaj Tarihi</th>
                <th>Bakiye (TL)</th>
              </tr>
            </thead>
            <tbody>
              {summaryData.map((data, index) => (
                <tr key={index}>
                  <td>{data.customer}</td>
                  <td>{data.lastMachine}</td>
                  <td>
                    {data.lastPuantajDate 
                      ? formatTrDateOnly(data.lastPuantajDate)
                      : '-'
                    }
                  </td>
                  <td>{Number(data.amount).toFixed(2)}</td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr>
                <td colSpan="3"><strong>Toplam</strong></td>
                <td>
                  <strong>{Number(totalAmount).toFixed(2)}</strong>
                </td>
              </tr>
            </tfoot>
          </table>
        </div>
      </div>

      {/* Mesai Raporu Operatör Seçim Modal'ı */}
      {isMesaiReportModalOpen && (
        <div className="modal-overlay" onClick={closeMesaiReportModal}>
          <div className="modal-content mesai-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Mesai Raporu - Operatör Seçin</h3>
              <button className="modal-close-btn" onClick={closeMesaiReportModal}>
                ✕
              </button>
            </div>
            <div className="modal-body">
              <p>Mesai raporu oluşturmak istediğiniz operatörü seçin:</p>
              <div className="operator-list">
                {operatorOptions.map((operator, index) => (
                  <div
                    key={index}
                    className="operator-item"
                    onClick={() => handleOperatorSelectForMesai(operator)}
                  >
                    <span className="operator-name">{operator.label}</span>
                    <span className="operator-arrow">→</span>
                  </div>
                ))}
              </div>
              {operatorOptions.length === 0 && (
                <p className="no-operators-message">Henüz operatör bulunamadı.</p>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default React.memo(Raporlama);

